/**
 * Mycelium Audit Trail Connector
 *
 * Subscribes to ALL domain events on the mycelium backbone and automatically
 * writes audit log entries. Like fungal networks carrying information —
 * it listens and records, never blocking critical flows.
 *
 * Design Principles:
 * - Economy of Movement: Services just publish events (which they already do),
 *   and audit trail grows naturally — no extra code required
 * - Mycelium Protocol: Pure subscriber pattern — zero coupling to source services
 * - Gravitational Organisation: Events gravitate to the audit trail through
 *   the backbone — not forced
 * - Holographic Universe: Each audit entry captures the full event context
 *
 * @module mycelium-audit-trail
 * @since 2026-02-21
 */

import type { MyceliumEvent, EventDomain } from './mycelium-event-backbone.service';
import { myceliumEventBackbone } from './mycelium-event-backbone.service';
import { logger } from '../lib/logger';
import { typedSupabase } from '../lib/typed-supabase';

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Event types that represent mutations and should always be audited.
 * Read-only operations are filtered by default unless explicitly included.
 */
const ALWAYS_AUDIT_PATTERNS = [
  // Mutation patterns
  'created', 'updated', 'deleted', 'removed', 'assigned', 'unassigned',
  'approved', 'rejected', 'cancelled', 'completed', 'failed',
  'shared', 'unshared', 'access_changed',
  // Auth/security patterns
  'role_changed', 'permission_granted', 'permission_revoked',
  'login', 'logout', 'password_changed',
  // Financial patterns
  'commission_calculated', 'payment_processed', 'invoice_',
  'xp_awarded',
  // Organization patterns
  'organization_joined', 'organization_left', 'team_assigned', 'team_removed',
] as const;

/**
 * Event types to explicitly SKIP auditing (high-frequency, low-value)
 */
const SKIP_AUDIT_PATTERNS = [
  'context_response', 'context_request', 'quick_actions_requested',
  'filtered_actions_available', 'ai_context_requested',
  'user_context_updated', 'user_context_enriched',
  'health_check', 'heartbeat',
] as const;

/**
 * Maximum batch size for audit writes
 */
const BATCH_SIZE = 10;

/**
 * Flush interval in milliseconds — 500ms for near-real-time activity feed
 */
const FLUSH_INTERVAL_MS = 500;

/**
 * Priority threshold for immediate flush — events at or above this
 * priority level bypass the timer and flush immediately
 */
const HIGH_PRIORITY_FLUSH_THRESHOLD = 7;

/**
 * Maximum character length for display messages in the activity feed
 */
const MAX_DISPLAY_MESSAGE_LENGTH = 200;

/**
 * Default icon when no domain or type match is found
 */
const DEFAULT_EVENT_ICON = '⚡';

// ============================================================================
// TYPES
// ============================================================================

interface AuditEntry {
  user_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: Record<string, unknown>;
  display_message: string;
  display_icon: string;
  feed_visible: boolean;
  actor_type: 'user' | 'ai_agent' | 'automation' | 'system';
  org_id: string | null;
}

// ============================================================================
// ICON MAPPINGS
// ============================================================================

/**
 * Domain-level icons — fallback when no type-specific icon matches
 */
const DOMAIN_ICONS: Record<string, string> = {
  task: '✅',
  sales: '💰',
  accounting: '📊',
  gamification: '🎮',
  notification: '🔔',
  ai: '🤖',
  calendar: '📅',
  user: '👤',
  system: '🔧',
  integration: '🔌',
  team: '👥',
  webhook: '🌐',
  crm: '🤝',
  document: '📄',
  cortex: '🧠',
  workload: '⚖️',
};

/**
 * Type-specific icons — matched by substring against event.type
 */
const TYPE_ICONS: Record<string, string> = {
  created: '✨',
  updated: '📝',
  deleted: '🗑️',
  completed: '✅',
  failed: '❌',
  approved: '👍',
  rejected: '👎',
  assigned: '📌',
  cancelled: '🚫',
  removed: '➖',
  login: '🔑',
  logout: '🚪',
  payment: '💳',
  invoice: '📋',
  xp_awarded: '⭐',
  commission: '💸',
  shared: '🔗',
};

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class AuditTrailConnector {
  private initialized = false;
  private auditBuffer: AuditEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private stats = {
    eventsAudited: 0,
    eventsSkipped: 0,
    flushCount: 0,
    errors: 0,
  };

  /**
   * Initialize connector and subscribe to ALL domain events
   * Subscribes with LOW priority — runs after business logic, never blocks
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug('AuditTrailConnector already initialized');
      return;
    }

    logger.info('📋 Audit Trail connector initializing');

    // Subscribe to ALL domains with LOW priority (observer pattern)
    myceliumEventBackbone.subscribe({
      name: 'AuditTrailConnector',
      // No domain filter — listen to everything
      handler: this.handleEvent.bind(this),
      priority: 1, // Lowest priority — always runs last
    });

    // Start periodic flush
    this.flushTimer = setInterval(() => {
      this.flushBuffer().catch(err => {
        logger.error('Audit trail flush failed', { error: err });
      });
    }, FLUSH_INTERVAL_MS);

    this.initialized = true;
    logger.info('✅ Audit Trail connector ready — observing all mycelium events');
  }

  /**
   * Handle incoming mycelium events
   * Economy of movement — one handler, all domains
   */
  private async handleEvent(event: MyceliumEvent): Promise<void> {
    try {
      // Filter: should this event be audited?
      if (!this.shouldAudit(event)) {
        this.stats.eventsSkipped++;
        return;
      }

      // Transform event to audit entry
      const entry = this.eventToAuditEntry(event);

      // Add to buffer for batch writing
      this.auditBuffer.push(entry);
      this.stats.eventsAudited++;

      // Immediate flush for high-priority events (priority >= 7) or full buffer
      if (event.metadata.priority >= HIGH_PRIORITY_FLUSH_THRESHOLD || this.auditBuffer.length >= BATCH_SIZE) {
        await this.flushBuffer();
      }
    } catch (error) {
      logger.error("❌ AuditTrail handler error", {
        eventId: event.id,
        eventType: event.type,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Determine if an event should be audited.
   * Economy of movement — quick checks, fail fast.
   */
  private shouldAudit(event: MyceliumEvent): boolean {
    const { type } = event;

    // Skip explicitly excluded patterns
    if (SKIP_AUDIT_PATTERNS.some(pattern => type.includes(pattern))) {
      return false;
    }

    // Always audit mutation patterns
    if (ALWAYS_AUDIT_PATTERNS.some(pattern => type.includes(pattern))) {
      return true;
    }

    // Default: audit everything else too (better to have data than not)
    // This is the holographic approach — capture everything
    return true;
  }

  /**
   * Transform a MyceliumEvent into an audit log entry.
   * Holographic — captures the full picture of the event.
   */
  private eventToAuditEntry(event: MyceliumEvent): AuditEntry {
    // Extract entity info from event payload
    const entityId = (event.payload.sourceId as string)
      || (event.payload.entityId as string)
      || (event.payload.id as string)
      || event.id;

    const entityType = this.resolveEntityType(event.domain, event.type);

    return {
      user_id: event.userId || null,
      action: `mycelium:${event.domain}:${event.type}`,
      entity_type: entityType,
      entity_id: entityId || null,
      details: {
        // Core event context
        event_id: event.id,
        domain: event.domain,
        type: event.type,
        timestamp: event.timestamp,
        // Organization and team context
        org_id: event.orgId || null,
        team_id: event.teamId || null,
        // Event source metadata
        source: event.metadata.source,
        processed_by: event.metadata.processedBy,
        tags: event.metadata.tags,
        priority: event.metadata.priority,
        // Relevant payload data (filtered to avoid PII overexposure)
        payload_summary: this.summarizePayload(event.payload),
      },
      // Activity feed fields
      display_message: this.generateDisplayMessage(event),
      display_icon: this.resolveIcon(event.domain, event.type),
      feed_visible: true,
      actor_type: this.resolveActorType(event),
      org_id: event.orgId || null,
    };
  }

  /**
   * Resolve a human-readable entity type from domain and event type
   */
  private resolveEntityType(domain: EventDomain, type: string): string {
    // Map event domains to entity types
    const entityTypeMap: Partial<Record<EventDomain, string>> = {
      task: 'task',
      sales: 'sales_transaction',
      accounting: 'invoice',
      gamification: 'xp_event',
      notification: 'notification',
      ai: 'ai_interaction',
      calendar: 'calendar_event',
      user: 'user',
      system: 'system',
      integration: 'integration',
      team: 'team',
    };

    return entityTypeMap[domain] || `${domain}_${type.split('_')[0]}`;
  }

  /**
   * Create a safe summary of event payload (avoid storing sensitive data)
   */
  private summarizePayload(payload: Record<string, unknown>): Record<string, unknown> {
    const summary: Record<string, unknown> = {};

    // Include safe fields only
    const safeKeys = [
      'status', 'action', 'result', 'count', 'total', 'amount',
      'sourceId', 'entityId', 'type', 'category',
      'oldValue', 'newValue', 'reason', 'description',
    ];

    for (const key of safeKeys) {
      if (key in payload) {
        summary[key] = payload[key];
      }
    }

    // Always include field count for context
    summary._fieldCount = Object.keys(payload).length;

    return summary;
  }

  /**
   * Resolve actor type from event source and domain.
   * Distinguishes human, AI, automation, and system actors.
   */
  private resolveActorType(event: MyceliumEvent): 'user' | 'ai_agent' | 'automation' | 'system' {
    const source = (event.metadata.source || '').toLowerCase();

    // AI/Cortex-originated events
    if (
      source.includes('brain') ||
      source.includes('cortex') ||
      event.domain === 'ai' ||
      event.domain === 'cortex'
    ) {
      return 'ai_agent';
    }

    // Automation-originated events
    if (
      source.includes('worker') ||
      source.includes('automation') ||
      source.includes('cron') ||
      source.includes('webhook')
    ) {
      return 'automation';
    }

    // System-level events
    if (
      source.includes('system') ||
      source.includes('health') ||
      event.domain === 'system'
    ) {
      return 'system';
    }

    // User-initiated if userId present
    if (event.userId) {
      return 'user';
    }

    return 'system';
  }

  /**
   * Resolve the best icon for this event.
   * Checks type-specific icons first, then falls back to domain icon.
   */
  private resolveIcon(domain: EventDomain, type: string): string {
    // Check type-specific icons first (more specific wins)
    for (const [pattern, icon] of Object.entries(TYPE_ICONS)) {
      if (type.includes(pattern)) {
        return icon;
      }
    }

    // Fall back to domain icon
    return DOMAIN_ICONS[domain] || DEFAULT_EVENT_ICON;
  }

  /**
   * Generate a human-readable display message for the activity feed.
   */
  private generateDisplayMessage(event: MyceliumEvent): string {
    const entityType = this.resolveEntityType(event.domain, event.type);

    // Make type human-readable: snake_case → Title Case
    const action = event.type
      .split('_')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Use payload description if available for richer context
    const description = event.payload.description;
    if (description && typeof description === 'string') {
      return `${action}: ${description}`.slice(0, MAX_DISPLAY_MESSAGE_LENGTH);
    }

    return `${action} — ${entityType}`;
  }

  /**
   * Flush buffered audit entries to the database
   * Batch writes for performance — economy of movement
   */
  private async flushBuffer(): Promise<void> {
    if (this.auditBuffer.length === 0) {
      return;
    }

    // Take current buffer and reset
    const entries = [...this.auditBuffer];
    this.auditBuffer = [];

    try {
      const { error } = await typedSupabase
        .from('audit_logs')
        .insert(entries.map(entry => ({
          user_id: entry.user_id,
          action: entry.action,
          entity_type: entry.entity_type,
          entity_id: entry.entity_id,
          details: entry.details as unknown as Record<string, unknown>,
          display_message: entry.display_message,
          display_icon: entry.display_icon,
          feed_visible: entry.feed_visible,
          actor_type: entry.actor_type,
          org_id: entry.org_id,
        })));

      if (error) {
        logger.error('Failed to flush audit entries', {
          error: error.message,
          count: entries.length,
        });
        this.stats.errors++;
        // Put entries back for retry (at the front)
        this.auditBuffer.unshift(...entries);
        return;
      }

      this.stats.flushCount++;

      logger.debug('📋 Audit trail flushed', {
        count: entries.length,
        totalAudited: this.stats.eventsAudited,
      });
    } catch (error) {
      logger.error('Audit trail flush exception', {
        error: error instanceof Error ? error.message : 'Unknown error',
        count: entries.length,
      });
      this.stats.errors++;
      // Put entries back for retry
      this.auditBuffer.unshift(...entries);
    }
  }

  /**
   * Get audit trail statistics
   */
  getStats(): typeof this.stats {
    return { ...this.stats };
  }

  /**
   * Cleanup on disposal — flush remaining entries first
   */
  async dispose(): Promise<void> {
    // Clear flush timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Flush remaining entries
    await this.flushBuffer();

    this.initialized = false;
    logger.info('📋 Audit Trail connector disposed', { stats: this.stats });
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const auditTrailConnector = new AuditTrailConnector();
